import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { UserRole, AuthResponse } from '@/api/types';
import client from '@/api/client';

interface AuthUser {
  userId: string;
  email: string | null;
  fullName: string;
  role: UserRole;
  themePreference: string;
  languagePreference: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName: string, phone?: string, companyName?: string, acceptedTerms?: boolean) => Promise<void>;
  registerTenant: (email: string | undefined, password: string, fullName: string, phone: string) => Promise<void>;
  logout: () => void;
  updateUser: (patch: Partial<AuthUser>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function restoreUser(): AuthUser | null {
  const raw = localStorage.getItem('authUser');
  if (!raw) return null;
  try { return JSON.parse(raw) as AuthUser; } catch { return null; }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(restoreUser);

  const login = useCallback(async (identifier: string, password: string) => {
    const data = await client.post<AuthResponse>('/api/auth/login', { identifier, password })
      .then(r => r as unknown as AuthResponse);
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    const authUser: AuthUser = {
      userId: data.userId,
      email: data.email,
      fullName: data.fullName,
      role: data.role,
      themePreference: data.themePreference ?? 'light',
      languagePreference: data.languagePreference ?? 'en',
    };
    localStorage.setItem('authUser', JSON.stringify(authUser));
    setUser(authUser);
  }, []);

  const updateUser = useCallback((patch: Partial<AuthUser>) => {
    setUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      localStorage.setItem('authUser', JSON.stringify(next));
      return next;
    });
  }, []);

  const register = useCallback(async (
    email: string, password: string, fullName: string,
    phone?: string, companyName?: string, acceptedTerms?: boolean
  ) => {
    await client.post('/api/auth/register', { email, password, fullName, phone, companyName, acceptedTerms });
  }, []);

  const registerTenant = useCallback(async (
    email: string | undefined, password: string, fullName: string, phone: string
  ) => {
    await client.post('/api/auth/tenant-register', { email: email || undefined, password, fullName, phone });
  }, []);

  const logout = useCallback(() => {
    localStorage.clear();
    setUser(null);
    window.location.href = '/login';
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, login, register, registerTenant, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
